import { isNullOrUndefined } from '@deltasierra/type-utilities';
import { ProtocolRelativeUrl } from '../common/lang/web';
import { MobileExportPlatformNames, MobileSupportedFormatTypes } from '../integration';
import { BuilderTemplateFormat } from '../builderTemplate/BuilderTemplateFormat';
import { Result, assertNever, resultFailure, resultSuccess } from '../common';
import {
    ImageLayer,
    Layer,
    MapLayer,
    TextLayer,
    VideoLayer,
    isImageLayer,
    isMapLayer,
    isTextLayer,
    isVideoLayer,
    LayerType,
    LocationType,
} from './layers';
import {
    BuilderDocument,
    BuilderEmailDocument,
    EditableField,
    TextSubstitutionField,
    TextSubstitutionValues,
    isContentBuilderDocument,
    isVideoDocument,
} from './builderDocument';
import { AutomaticTextPlaceholders } from './textSubstitution';
import { BuilderPrintDocument } from './print';

export const MobileSupportedEditableFieldPaths = [
    'blendMode',
    'displayPin',
    'fill',
    'fontFamily',
    'fontSize',
    'height',
    'letterSpacing',
    'lineHeight',
    'location',
    'lowercase',
    'maxLength',
    'opacity',
    'position.x',
    'position.y',
    'rotation',
    'skewX',
    'skewY',
    'stroke',
    'strokeWidth',
    'text',
    'textAlign',
    'textFit',
    'textVerticalAlign',
    'uppercase',
    'width',
];

export interface BuilderDocumentResources {
    images: ProtocolRelativeUrl[];
    fonts: string[];
    videos: ProtocolRelativeUrl[];
}

function isExternalResource(locationType: LocationType, location: string | null): location is string {
    return locationType === LocationType.external && !!location;
}

export function isMobileSupportedEditableField(field: EditableField): boolean {
    return MobileSupportedEditableFieldPaths.some(x => x === field.path);
}

export enum BuilderDocumentMobileValidationFailure {
    UnsupportedDocumentFormat,
    UnsupportedEditableField,
    AnimationPresent,
    UnsupportedBuilderTemplateFormat,
    UnsupportedMultiImageTemplate,
}

export interface IsValidMobileTemplateFailedResult {
    errors: BuilderDocumentMobileValidationFailure[];
    warnings: BuilderDocumentMobileValidationFailure[];
}

const ALL_AUTOMATIC_TEXT_SUBSTITUTION_TYPES: AutomaticTextPlaceholders[] = [
    AutomaticTextPlaceholders.Location,
    AutomaticTextPlaceholders.Phone,
    AutomaticTextPlaceholders.Email,
    AutomaticTextPlaceholders.Address,
    AutomaticTextPlaceholders.AddressSingleLine,
    AutomaticTextPlaceholders.Website,
];

// NOTE: not translatable! Oh well.
const AutomaticTextPlaceholderDisplayNames: {
    [K in AutomaticTextPlaceholders]: string;
} = {
    [AutomaticTextPlaceholders.Location]: 'Location',
    [AutomaticTextPlaceholders.Phone]: 'Phone',
    [AutomaticTextPlaceholders.Email]: 'Email',
    [AutomaticTextPlaceholders.Address]: 'Address',
    [AutomaticTextPlaceholders.AddressSingleLine]: 'Address (single line)',
    [AutomaticTextPlaceholders.Website]: 'Website',
};

export class BuilderDocumentInspector {
    public identifyExternalResources(document: BuilderDocument): BuilderDocumentResources {
        const resources: BuilderDocumentResources = {
            fonts: [],
            images: [],
            videos: [],
        };
        for (const layer of document.layers) {
            if ((isImageLayer(layer) || isMapLayer(layer)) && isExternalResource(layer.locationType, layer.location)) {
                resources.images.push(ProtocolRelativeUrl.from(layer.location));
            } else if (isTextLayer(layer) && layer.fontFamily && !resources.fonts.includes(layer.fontFamily)) {
                resources.fonts.push(layer.fontFamily);
            } else if (isVideoLayer(layer) && isExternalResource(layer.locationType, layer.location)) {
                resources.videos.push(ProtocolRelativeUrl.from(layer.location));
            }
        }
        return resources;
    }

    public hasAnyAutomaticSubstitutionContent(document: BuilderDocument): boolean {
        return (
            this.hasAnyLocationAutomaticTextSubstitutionContent(document) ||
            document.layers.some(x => isImageLayer(x) && !isNullOrUndefined(x.substitutionType))
        );
    }

    public hasAnyLocationAutomaticTextSubstitutionContent(document: BuilderDocument): boolean {
        return this.hasTextSubstitutionContent(document, ALL_AUTOMATIC_TEXT_SUBSTITUTION_TYPES);
    }

    public hasTextSubstitutionContent(document: BuilderDocument, keys: string[]): boolean {
        const textLayers = this.getLayersByType(document, LayerType.text);
        for (const layer of textLayers) {
            if (this.layerHasSomeTextSubstitutionContent(layer, keys)) {
                return true;
            }
        }

        return false;
    }

    public isMobileCompatible(
        document: BuilderDocument | BuilderEmailDocument | BuilderPrintDocument,
        formats: BuilderTemplateFormat[],
    ): boolean {
        if (isContentBuilderDocument(document) || isVideoDocument(document)) {
            const result = this.validateDocumentForMobile(document, formats);
            switch (result.success) {
                case true:
                    return true;
                case false:
                    // A template is not compatible if it has errors. We allow warnings.
                    return result.failure.errors.length === 0;
                default:
                    throw assertNever(result);
            }
        } else {
            return false;
        }
    }

    // eslint-disable-next-line max-statements
    public validateDocumentForMobile(
        doc: BuilderDocument,
        formats: BuilderTemplateFormat[],
    ): Result<IsValidMobileTemplateFailedResult, undefined> {
        const errors: BuilderDocumentMobileValidationFailure[] = [];
        const warnings: BuilderDocumentMobileValidationFailure[] = [];

        if (!isContentBuilderDocument(doc) && !isVideoDocument(doc)) {
            errors.push(BuilderDocumentMobileValidationFailure.UnsupportedDocumentFormat);
        } else {
            if (doc.editableFields.length > 0) {
                const hasUnsupportedFields = doc.editableFields.some(field => !isMobileSupportedEditableField(field));
                if (hasUnsupportedFields) {
                    errors.push(BuilderDocumentMobileValidationFailure.UnsupportedEditableField);
                }
            }
            if (doc.animation) {
                errors.push(BuilderDocumentMobileValidationFailure.AnimationPresent);
            }
            if (doc.multiImage) {
                errors.push(BuilderDocumentMobileValidationFailure.UnsupportedMultiImageTemplate);
            }

            let hasAtLeastOneSupportedFormat = false;
            let hasFormatFailure = false;
            for (const format of formats) {
                if (!MobileSupportedFormatTypes.includes(format.type)) {
                    hasFormatFailure = true;
                } else if (format.canPublishTo) {
                    if (MobileExportPlatformNames.includes(format.canPublishTo)) {
                        hasAtLeastOneSupportedFormat = true;
                    } else {
                        hasFormatFailure = true;
                    }
                }
            }
            if (hasFormatFailure) {
                let destArray = errors;
                if (hasAtLeastOneSupportedFormat) {
                    destArray = warnings;
                }
                destArray.push(BuilderDocumentMobileValidationFailure.UnsupportedBuilderTemplateFormat);
            }
        }

        if (errors.length) {
            return resultFailure({
                errors,
                warnings,
            });
        }

        return resultSuccess(undefined);
    }

    public getVideoLayers(doc: BuilderDocument): VideoLayer[] {
        return this.getLayersByType(doc, LayerType.video);
    }

    public shouldRemoveAudioForVideoOutput(doc: BuilderDocument): boolean {
        const videoLayers: VideoLayer[] = this.getVideoLayers(doc);
        for (const layer of videoLayers) {
            if (layer.removeAudio === true) {
                return true;
            }
        }
        return false;
    }

    public getAllEditableTextValues(doc: BuilderDocument, textSubstitutionValues: TextSubstitutionValues): string[] {
        // Include all text substitutions that have been used in the document.
        const usedTextSubstitutions: string[] = this.getUsedTextSubstitutionFieldsIncludingAutomatic(doc)
            .map(field => {
                const value = textSubstitutionValues[field.key];
                if (value) {
                    return `${field.displayName}: ${value}`;
                } else {
                    return undefined;
                }
            })
            .filter((value): value is string => !!value);
        // Include text layer text content, if the layer is visible and editable from the simple editor.
        const textLayers = this.getLayersByType(doc, LayerType.text);
        return textLayers
            .filter(layer => layer.visible && this.hasEditableLayerField(doc, layer.id, 'text'))
            .map(layer => layer.text)
            .concat(usedTextSubstitutions);
    }

    protected layerHasSomeTextSubstitutionContent(layer: TextLayer, keys: string[]): boolean {
        return keys.some(key => layer.text.includes(`{${key}}`));
    }

    protected getUsedTextSubstitutionFieldsIncludingAutomatic(doc: BuilderDocument): TextSubstitutionField[] {
        return (
            doc.textSubstitutionFields
                /*
                 * The "location" placeholder is automatically added as a text substitution field, but is also an
                 * "automatic" text substitution field. Filter out one of them to avoid duplicates.
                 */
                // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
                .filter(field => field.key === AutomaticTextPlaceholders.Location)
                .concat(
                    ALL_AUTOMATIC_TEXT_SUBSTITUTION_TYPES.map(key => ({
                        displayName: AutomaticTextPlaceholderDisplayNames[key],
                        key,
                    })),
                )
                .filter(field => this.hasTextSubstitutionContent(doc, [field.key]))
        );
    }

    protected getLayersByType(doc: BuilderDocument, type: LayerType.ellipse | LayerType.rect): Layer[];

    protected getLayersByType(doc: BuilderDocument, type: LayerType.image): ImageLayer[];

    protected getLayersByType(doc: BuilderDocument, type: LayerType.map): MapLayer[];

    protected getLayersByType(doc: BuilderDocument, type: LayerType.text): TextLayer[];

    protected getLayersByType(doc: BuilderDocument, type: LayerType.video): VideoLayer[];

    protected getLayersByType<T extends Layer>(doc: BuilderDocument, type: LayerType): T[] {
        return doc.layers.filter((layer: Layer) => layer.type === type) as T[];
    }

    protected hasEditableLayerField(doc: BuilderDocument, layerId: number, path: string): boolean {
        for (const editableField of doc.editableFields) {
            if ('layerId' in editableField && editableField.layerId === layerId && editableField.path === path) {
                return true;
            }
        }
        return false;
    }
}
